import { useState } from 'react';
import { FaRegTrashCan } from 'react-icons/fa6';
import { IoCopyOutline, IoCheckmarkOutline } from 'react-icons/io5';
import { CustomButton } from '@/components/uiWrappers/button';
import { Badge } from '@/components/ui/badge';
import { ArticleData, ArticleStatus, ArticleAssignmentBucket } from '@mightybyte/business';
import { ArticleFormModal } from './ArticleFormModal';
import { DeleteArticleModal } from './DeleteArticleModal';

interface ArticleCardProps {
  article: ArticleData;
}

const STATUS_BADGE: Record<ArticleStatus, { label: string; className: string }> = {
  [ArticleStatus.Published]: { label: 'Published', className: 'bg-brand' },
  [ArticleStatus.Draft]: { label: 'Draft', className: 'bg-gray-400' },
  [ArticleStatus.Hidden]: { label: 'Hidden', className: 'bg-amber-500' },
};

const BUCKET_LABELS: Record<ArticleAssignmentBucket, string> = {
  [ArticleAssignmentBucket.General]: 'General',
  [ArticleAssignmentBucket.One]: 'Bucket 1',
  [ArticleAssignmentBucket.Two]: 'Bucket 2',
};

export function ArticleCard({ article }: ArticleCardProps) {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    const link = `parentingfirst://articles/article?articleId=${article._id}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const badge = STATUS_BADGE[article.status];
  const titleColor = article.titleColor ?? '#ffffff';

  return (
    <>
      <ArticleFormModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        article={article}
      />
      <DeleteArticleModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        articleId={article._id}
        articleTitle={article.title}
      />

      <div className="rounded-[20px] overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-gray-200 flex flex-col w-[280px]">

        {/* Image / header section */}
        <div className="relative h-[180px] flex-shrink-0">
          {article.imageUrl ? (
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div
              className="w-full h-full"
              style={{
                backgroundColor:
                  article.status === ArticleStatus.Published
                    ? 'var(--quiz-bg-published)'
                    : 'var(--quiz-bg-draft)',
              }}
            />
          )}

          {/* Gradient overlay for title readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />

          {/* Status badge */}
          <Badge className={`${badge.className} absolute right-3 top-3`}>
            {badge.label}
          </Badge>

          {/* Title — bottom-aligned on image */}
          <h3
            className="absolute bottom-3 left-3 right-3 font-bold text-base line-clamp-2 leading-snug"
            style={{ color: titleColor }}
          >
            {article.title}
          </h3>
        </div>

        {/* Description */}
        <div className="px-3 pt-3 pb-1 flex-1">
          {article.shortDescription ? (
            <p className="text-sm text-gray-600 line-clamp-2">
              {article.shortDescription}
            </p>
          ) : (
            <p className="text-sm text-gray-400 italic line-clamp-2">No description</p>
          )}

          {/* Module assignments */}
          {article.moduleAssignments.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {article.moduleAssignments.map((a, i) => (
                <span
                  key={i}
                  className="text-xs bg-gray-100 rounded px-1.5 py-0.5 text-gray-700 font-medium"
                >
                  M{a.moduleNumber} · {BUCKET_LABELS[a.bucket]}{a.order ? ` · #${a.order}` : ''}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Bottom actions */}
        <div className="bg-white px-3 py-3 flex items-center gap-3">
          <CustomButton
            variant="outline"
            className="flex-1"
            onClick={() => setIsEditOpen(true)}
          >
            Edit
          </CustomButton>
          <div className="relative group">
            <button
              onClick={handleCopyLink}
              className={`w-[38px] h-[38px] rounded-lg transition-colors justify-center items-center flex cursor-pointer ${copied ? 'text-green-600 bg-green-50' : 'text-brand bg-brand/10'}`}
            >
              {copied ? <IoCheckmarkOutline className="w-5 h-5" /> : <IoCopyOutline className="w-5 h-5" />}
            </button>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              {copied ? 'Copied!' : 'Copy app link'}
            </div>
          </div>
          <button
            onClick={() => setIsDeleteOpen(true)}
            className="w-[38px] h-[38px] text-red-500 bg-red-50 rounded-lg transition-colors justify-center items-center flex cursor-pointer"
            title="Delete article"
          >
            <FaRegTrashCan className="w-5 h-5" />
          </button>
        </div>

      </div>
    </>
  );
}